import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CancelIcon from "@mui/icons-material/Cancel";
import { CircularProgress, TextField, IconButton } from "@mui/material";
import { useState } from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { AutoToggle } from "@/Chat/ChatSidebar/ChatInputBox/AutoToggle.tsx";
import { ClientToolSettingsModal } from "@/Chat/ChatSidebar/ClientToolSettingsModal";
import { useModelProfiles } from "@/shared/domain/chat/hooks/useModelProfiles";

import { AIContextSettingsModal } from "./AIContextSettingsModal";
import styles from "./ChatInputBox.module.css";
import { ContextWidget } from "./ContextWidget";
import { ModelProfileSelector } from "./ModelProfileSelector";
import { MoreOptionsButton } from "./MoreOptionsButton";

interface ChatInputBoxProps {
  onSubmit: (message: string) => Promise<void>;
  onCancel: () => void | Promise<void>;
  loading: boolean;
  isCancelling?: boolean;
  chatModelProfile?: string;
}

/**
 * Pure ChatInput component that accepts callbacks.
 * All data fetching and mutations are handled by the parent Chat component.
 */
export const ChatInputBox = ({
  onSubmit,
  onCancel,
  loading,
  isCancelling = false,
  chatModelProfile,
}: ChatInputBoxProps) => {
  const {
    context,
    contextDisplayNames,
    handleContextDelete,
    autoEnabled,
    handleToggleAuto,
    modelProfile,
    setModelProfile,
  } = useAIChat();

  const { data: profiles = [] } = useModelProfiles();

  // Resolve the active profile: existing chat's profile, user selection, or API default
  const defaultProfileId = profiles.find((p) => p.is_default)?.id;
  const activeProfile =
    chatModelProfile ?? modelProfile ?? defaultProfileId ?? "";
  const isProfileLocked = chatModelProfile !== undefined;
  const [message, setMessage] = useState("");
  const [toolSettingsOpen, setToolSettingsOpen] = useState(false);
  const [contextSettingsOpen, setContextSettingsOpen] = useState(false);

  const handleSend = () => {
    if (message.trim() && !loading && !isCancelling) {
      // If user hasn't explicitly selected a profile, send the default
      if (modelProfile === undefined && defaultProfileId) {
        setModelProfile(defaultProfileId);
      }
      void onSubmit(message);
      setMessage("");
    }
  };

  return (
    <div className={styles.centeringContainer}>
      <div className={styles.topContainer}>
        <ContextWidget
          context={context}
          displayNames={contextDisplayNames}
          onDelete={handleContextDelete}
        />

        <div className={styles.textFieldContainer}>
          <TextField
            fullWidth
            multiline
            maxRows={8}
            minRows={3}
            placeholder="Say something to Roboto's AI assistant"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className={styles.inputField}
            inputProps={{
              style: { fontSize: "0.875rem" },
              "aria-label": "Chat message input",
            }}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: "none",
                },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: "none",
                },
              "& .MuiOutlinedInput-root": {
                padding: 0,
              },
            }}
          />
          <div className={styles.controlsRow}>
            <div className={styles.controlsLeft}>
              <MoreOptionsButton
                onOpenToolSettings={() => setToolSettingsOpen(true)}
                onOpenContextSettings={() => setContextSettingsOpen(true)}
              />
              <AutoToggle enabled={autoEnabled} onToggle={handleToggleAuto} />
            </div>
            {loading && (
              <IconButton
                size="small"
                aria-label={isCancelling ? "Cancelling..." : "Cancel message"}
                onClick={() => void onCancel()}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <CircularProgress size={16} />
                ) : (
                  <CancelIcon className={styles.smallIcon} />
                )}
              </IconButton>
            )}
            {!loading && (
              <div className={styles.sendGroup}>
                <ModelProfileSelector
                  profiles={profiles}
                  value={activeProfile}
                  onChange={setModelProfile}
                  disabled={isProfileLocked}
                />
                <IconButton
                  size="small"
                  onClick={handleSend}
                  disabled={!message.trim()}
                  aria-label="Send message"
                >
                  <ArrowUpwardIcon className={styles.smallIcon} />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>

      <ClientToolSettingsModal
        open={toolSettingsOpen}
        onClose={() => setToolSettingsOpen(false)}
      />
      <AIContextSettingsModal
        open={contextSettingsOpen}
        onClose={() => setContextSettingsOpen(false)}
      />
    </div>
  );
};
